import React, { useState, useEffect, useRef } from 'react';
import {
  Box, VStack, Text, Button, Flex, IconButton, useToast, Image, Progress,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2, FiFile } from 'react-icons/fi';
import api from '../api';

export default function FileUpload({ entityType, entityId, folder = 'documents', accept = 'image/*,.pdf', label = 'Attachments', multiple = true, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef(null);
  const toast = useToast();

  const fetchFiles = async () => {
    if (!entityType || !entityId) return;
    try {
      const { data } = await api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`);
      setFiles(data);
    } catch (e) {}
  };

  useEffect(() => { fetchFiles(); }, [entityType, entityId]);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    let uploaded = 0;
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        const { data } = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
        });
        if (onUploaded && data) { try { onUploaded(data); } catch(e) {} }
        uploaded++;
        setUploadProgress(Math.round((uploaded / selectedFiles.length) * 100));
      }
      await fetchFiles();
      toast({ title: `${uploaded} file${uploaded > 1 ? 's' : ''} uploaded`, status: 'success', duration: 1500 });
    } catch (err) {
      toast({ title: err.response?.data?.error || 'Upload failed', status: 'error', duration: 3000 });
      if (uploaded > 0) await fetchFiles();
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      await fetchFiles();
      toast({ title: 'File removed', status: 'info', duration: 1500 });
    } catch (e) {}
  };

  const fileUrl = (f) => `/api/files/${f.folder}/${f.stored_name}`;
  const isImage = (f) => f.mime_type?.startsWith('image/');

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={1}>
        <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em"
          bg="gray.800" color="white" px={1.5} py={0.5} borderRadius="sm" display="inline-block">
          {label}
        </Text>
        <Button size="xs" leftIcon={<FiUpload />} variant="ghost" onClick={() => fileRef.current?.click()}
          isLoading={uploading} loadingText="Uploading...">
          Upload
        </Button>
      </Flex>
      <input type="file" ref={fileRef} style={{ display: 'none' }} accept={accept} multiple={multiple} onChange={handleUpload} />
      {uploading && <Progress size="xs" value={uploadProgress} colorScheme="blue" mb={1} />}
      {files.length > 0 && (
        <VStack spacing={1} align="stretch">
          {files.map(f => (
            <Flex key={f.id} p={1.5} border="1px" borderColor="gray.100" borderRadius="md"
              align="center" gap={2} fontSize="11px">
              {isImage(f) ? (
                <Image src={fileUrl(f)} boxSize="28px" objectFit="cover" borderRadius="sm" cursor="pointer"
                  onClick={() => window.open(fileUrl(f), '_blank')} />
              ) : (
                <Box cursor="pointer" onClick={() => window.open(fileUrl(f), '_blank')}><FiFile size={16} /></Box>
              )}
              <Text flex={1} noOfLines={1} cursor="pointer" onClick={() => window.open(fileUrl(f), '_blank')}>
                {f.original_name}
              </Text>
              <Text color="gray.400" fontSize="9px">{(f.file_size / 1024).toFixed(0)}KB</Text>
              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                onClick={() => handleDelete(f.id)} aria-label="Delete" />
            </Flex>
          ))}
        </VStack>
      )}
    </Box>
  );
}
